import { FRAG_GRADIENT, VERT } from "./shaders";

/*
 * The WebGL helpers that the prototype's three canvases (#stage in app.js,
 * #featuredworkgl in featuredwork.js, #halogl in halo.js) each carried a copy
 * of. Plain functions on a WebGL2 context; the React host is GLCanvas.
 */

export type RGB = [number, number, number];

const TAU = Math.PI * 2;

/* ------------------------------------------------------------- colour/hash */

export const hexToRgb = (hex: string): RGB => {
    let h = hex.replace("#", "");
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const v = parseInt(h, 16);
    return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
};

/** Deterministic 0..1 per-index noise, the same one the shaders' poles use. */
export const hash1 = (n: number) => {
    const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
};

/* Luma-preserving hue rotation in RGB. An HSV round trip would do the same job
   but wraps badly near the blue/cyan boundary, which is where the Work palette
   lives. Same matrix as FRAG_GRADIENT's uCurHue. */
export const rotateHue = (
    r: number,
    g: number,
    b: number,
    deg: number,
): RGB => {
    const a = (deg * Math.PI) / 180;
    const c = Math.cos(a);
    const s = Math.sin(a);
    return [
        r * (0.213 + c * 0.787 - s * 0.213) +
            g * (0.715 - c * 0.715 - s * 0.715) +
            b * (0.072 - c * 0.072 + s * 0.928),
        r * (0.213 - c * 0.213 + s * 0.143) +
            g * (0.715 + c * 0.285 + s * 0.14) +
            b * (0.072 - c * 0.072 - s * 0.283),
        r * (0.213 - c * 0.213 - s * 0.787) +
            g * (0.715 - c * 0.715 + s * 0.715) +
            b * (0.072 + c * 0.928 + s * 0.072),
    ];
};

/* ---------------------------------------------------------------- programs */

export interface GLProgram {
    program: WebGLProgram;
    /** Uniform locations by name; arrays are keyed without their `[0]`. */
    uniforms: Record<string, WebGLUniformLocation | null>;
}

const numbered = (src: string) =>
    src
        .split("\n")
        .map((line, i) => `${i + 1}: ${line}`)
        .join("\n");

export const compileShader = (
    gl: WebGL2RenderingContext,
    type: GLenum,
    src: string,
) => {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("createShader failed");
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(shader) ?? "";
        gl.deleteShader(shader);
        throw new Error(`Shader compile failed: ${log}\n${numbered(src)}`);
    }
    return shader;
};

/** Compile and link a program, and cache every active uniform's location. */
export const createProgram = (
    gl: WebGL2RenderingContext,
    fragSrc: string,
    vertSrc: string = VERT,
): GLProgram => {
    const vert = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    let frag: WebGLShader;
    try {
        frag = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    } catch (err) {
        gl.deleteShader(vert);
        throw err;
    }
    const program = gl.createProgram();
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    // the linked program keeps what it needs; the shader objects can go
    gl.deleteShader(vert);
    gl.deleteShader(frag);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const log = gl.getProgramInfoLog(program) ?? "";
        gl.deleteProgram(program);
        throw new Error(`Program link failed: ${log}`);
    }
    const uniforms: GLProgram["uniforms"] = {};
    const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
    for (let i = 0; i < count; i++) {
        const info = gl.getActiveUniform(program, i);
        if (!info) continue;
        const name = info.name.replace(/\[0\]$/, "");
        uniforms[name] = gl.getUniformLocation(program, name);
    }
    return { program, uniforms };
};

/** FRAG_GRADIENT with its pole count filled in. */
export const gradientSource = (nPoles: number) =>
    FRAG_GRADIENT.replace("__NPOLES__", String(nPoles));

/* VERT builds its triangle from gl_VertexID, so it needs no buffers, but WebGL2
   still wants a vertex array bound before drawing. */
export const createFullscreenVAO = (gl: WebGL2RenderingContext) => {
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    return vao;
};

export const drawFullscreen = (gl: WebGL2RenderingContext) =>
    gl.drawArrays(gl.TRIANGLES, 0, 3);

/* ---------------------------------------------------------- render targets */

export interface TargetFormat {
    internalFormat: GLenum;
    type: GLenum;
}

export interface RenderTarget {
    tex: WebGLTexture;
    fbo: WebGLFramebuffer;
    format: TargetFormat;
    w: number;
    h: number;
}

/* Half-float targets when the GPU can render to them, because the gradient's
   long soft falloffs band at 8 bits once blurred. Falls back to RGBA8. */
export const getTargetFormat = (gl: WebGL2RenderingContext): TargetFormat => {
    const hasFloat = !!gl.getExtension("EXT_color_buffer_float");
    if (hasFloat) gl.getExtension("OES_texture_float_linear");
    return hasFloat
        ? { internalFormat: gl.RGBA16F, type: gl.HALF_FLOAT }
        : { internalFormat: gl.RGBA8, type: gl.UNSIGNED_BYTE };
};

/** A linear-filtered, edge-clamped texture with a framebuffer. Size it with sizeRenderTarget. */
export const createRenderTarget = (
    gl: WebGL2RenderingContext,
    format: TargetFormat = getTargetFormat(gl),
): RenderTarget => {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        tex,
        0,
    );
    return { tex, fbo, format, w: 0, h: 0 };
};

/** Reallocate the target's storage; a no-op when the size is unchanged. */
export const sizeRenderTarget = (
    gl: WebGL2RenderingContext,
    target: RenderTarget,
    w: number,
    h: number,
) => {
    if (target.w === w && target.h === h) return;
    target.w = w;
    target.h = h;
    gl.bindTexture(gl.TEXTURE_2D, target.tex);
    gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        target.format.internalFormat,
        w,
        h,
        0,
        gl.RGBA,
        target.format.type,
        null,
    );
};

export const deleteRenderTarget = (
    gl: WebGL2RenderingContext,
    target: RenderTarget,
) => {
    gl.deleteFramebuffer(target.fbo);
    gl.deleteTexture(target.tex);
};

/** Draw into a target (sets the viewport to its size). */
export const bindRenderTarget = (
    gl: WebGL2RenderingContext,
    target: RenderTarget,
) => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    gl.viewport(0, 0, target.w, target.h);
};

/**
 * Size the gradient target and the blur ping-pong pair for a drawing buffer of
 * `w`×`h` device px. Every canvas runs the gradient at CONFIG.render.gradScale
 * and the blur at CONFIG.render.blurScale of the canvas.
 */
export const sizePassTargets = (
    gl: WebGL2RenderingContext,
    targets: { grad: RenderTarget; ping: RenderTarget; pong: RenderTarget },
    w: number,
    h: number,
    gradScale: number,
    blurScale: number,
) => {
    sizeRenderTarget(
        gl,
        targets.grad,
        Math.max(2, Math.round(w * gradScale)),
        Math.max(2, Math.round(h * gradScale)),
    );
    const bw = Math.max(2, Math.round(w * blurScale));
    const bh = Math.max(2, Math.round(h * blurScale));
    sizeRenderTarget(gl, targets.ping, bw, bh);
    sizeRenderTarget(gl, targets.pong, bw, bh);
};

/**
 * The separable blur chain every canvas runs over its gradient: `iterations`
 * horizontal+vertical FRAG_BLUR passes, src → ping → pong, with geometric step
 * sizes (1, 2, 4…) so a wide blur takes few passes. `radiusTexels` is the total
 * radius in blur-target texels (CSS px × dpr × blurScale). Returns the target
 * holding the result (`pong`, or `src` itself if it was never blurred).
 */
export const blurChain = (
    gl: WebGL2RenderingContext,
    blur: GLProgram,
    src: RenderTarget,
    ping: RenderTarget,
    pong: RenderTarget,
    radiusTexels: number,
    iterations: number,
) => {
    const u = blur.uniforms;
    gl.useProgram(blur.program);
    gl.uniform1i(u.uTex, 0);
    gl.activeTexture(gl.TEXTURE0);

    const iters = Math.max(1, Math.round(iterations));
    let denom = 0;
    for (let i = 0; i < iters; i++) denom += Math.pow(2, i);
    const base = radiusTexels / Math.max(denom, 1e-4);

    let from = src;
    for (let i = 0; i < iters; i++) {
        const step = Math.max(base * Math.pow(2, i), 1.0);

        bindRenderTarget(gl, ping);
        gl.bindTexture(gl.TEXTURE_2D, from.tex);
        gl.uniform2f(u.uRes, ping.w, ping.h);
        gl.uniform2f(u.uDir, step, 0);
        drawFullscreen(gl);

        bindRenderTarget(gl, pong);
        gl.bindTexture(gl.TEXTURE_2D, ping.tex);
        gl.uniform2f(u.uRes, pong.w, pong.h);
        gl.uniform2f(u.uDir, 0, step);
        drawFullscreen(gl);

        from = pong;
    }
    return from;
};

/* --------------------------------------------------------- pole simulation */

/* Orbits and breathing are constant across the screen, so they run on the CPU
   rather than per pixel. Two position sets: the silhouette barely drifts, the
   colour poles run sustained elliptical orbits. Sustained is the key word -- a
   noise random-walk wanders and settles, which is why the motion used to decay
   to a standstill. */

export interface Pole {
    /** p-space position */
    x: number;
    y: number;
    r: number;
    color: string;
}

/** The fields of CONFIG.blob, FEATUREDWORK and HALO that drive the poles. */
export interface PoleMotion {
    orbitPeriod: number;
    orbitAmp: number;
    colorSpread: number;
    shapePeriodMul: number;
    shapeDrift: number;
    shapeRound?: number;
    radiusBreath: number;
    radiusBreathPeriod: number;
    brightBreath: number;
    satBreath: number;
    breathPeriod: number;
    paletteWash?: number;
    /** Degrees either side of each pole's hue. Only FEATUREDWORK sets it. */
    hueBreath?: number;
    hueBreathPeriod?: number;
}

export interface PoleOptions {
    /*
     * How much shapeRound fattens each pole's radius. The hero (app.js) uses
     * 0.42; Work and Halo use 0.5. This is the only numeric difference between
     * the three copies.
     */
    roundRadiusGain?: number;
}

/** Uniform arrays for FRAG_GRADIENT: uShapePos, uColorPos, uPoleRad, uPoleCol. */
export interface PoleBuffers {
    shapePos: Float32Array;
    colorPos: Float32Array;
    poleRad: Float32Array;
    poleCol: Float32Array;
}

export const createPoleBuffers = (n: number): PoleBuffers => ({
    shapePos: new Float32Array(n * 2),
    colorPos: new Float32Array(n * 2),
    poleRad: new Float32Array(n),
    poleCol: new Float32Array(n * 3),
});

/**
 * Advance the poles to time `t` (seconds) and write them into `out`.
 *
 * The prototype's three copies share this core. Their differences: the hero
 * gains 0.42 of shapeRound on the radius where Work and Halo gain 0.5
 * (`roundRadiusGain`); only Work applies `hueBreath`; and the hero clamps the
 * orbit, shape, radius and breath periods to a floor (0.5s, shapePeriodMul
 * 0.1) that none of the shipped configs come near, so it is applied for all.
 * The hero's whole-blob wander is not part of this; it moves uOffX/uOffY.
 */
export const simulatePoles = (
    t: number,
    poles: readonly Pole[],
    motion: PoleMotion,
    bg: RGB,
    out: PoleBuffers,
    { roundRadiusGain = 0.5 }: PoleOptions = {},
) => {
    const n = poles.length;
    const M = motion;

    // centroid of the colour poles, so colorSpread pushes them apart about
    // their shared centre rather than sliding the group toward one corner
    let ccx = 0;
    let ccy = 0;
    for (const pl of poles) {
        ccx += pl.x;
        ccy += pl.y;
    }
    ccx /= n;
    ccy /= n;
    const round = M.shapeRound ?? 0;
    const wash = M.paletteWash ?? 0;

    for (let i = 0; i < n; i++) {
        const pl = poles[i];
        const h1 = hash1(i + 1.0);
        const h2 = hash1(i + 8.3);
        const h3 = hash1(i + 17.7);
        const h4 = hash1(i + 29.1);

        // --- colour pole: sustained elliptical orbit, incommensurate period
        const per = Math.max(M.orbitPeriod * (0.72 + 0.56 * h1), 0.5);
        const ph = h2 * TAU;
        const a = M.orbitAmp * (0.65 + 0.7 * h3);
        const b = a * (0.45 + 0.6 * h4);
        const rot = h1 * TAU;
        const w = (TAU * t) / per + ph;
        const ex = Math.cos(w) * a;
        const ey = Math.sin(w) * b;
        out.colorPos[i * 2] =
            ccx +
            (pl.x - ccx) * M.colorSpread +
            ex * Math.cos(rot) -
            ey * Math.sin(rot);
        out.colorPos[i * 2 + 1] =
            ccy +
            (pl.y - ccy) * M.colorSpread +
            ex * Math.sin(rot) +
            ey * Math.cos(rot);

        // --- silhouette pole: far smaller, far slower. shapeRound pulls them
        // toward their shared centre, which rounds the outline without moving
        // or resizing the blob overall.
        const sw =
            (TAU * t) / (per * Math.max(M.shapePeriodMul, 0.1)) + ph * 0.6;
        out.shapePos[i * 2] =
            ccx + (pl.x - ccx) * (1 - round) + Math.cos(sw) * M.shapeDrift;
        out.shapePos[i * 2 + 1] =
            ccy +
            (pl.y - ccy) * (1 - round) +
            Math.sin(sw) * M.shapeDrift * 0.7;

        // --- expansion / contraction
        const rp = Math.max(M.radiusBreathPeriod * (0.8 + 0.4 * h3), 0.5);
        out.poleRad[i] =
            pl.r *
            (1 + round * roundRadiusGain) *
            (1 + M.radiusBreath * Math.sin((TAU * t) / rp + ph));

        // --- colour breathing, each pole on its own schedule. This is what
        // shifts the composition's colour balance over time, rather than every
        // region brightening together.
        const c = hexToRgb(pl.color);
        const bp = Math.max(M.breathPeriod, 0.5);
        const bb =
            1 +
            M.brightBreath *
                Math.sin((TAU * t) / (bp * (0.8 + 0.5 * h2)) + ph * 1.3);
        const sb =
            1 +
            M.satBreath *
                Math.sin((TAU * t) / (bp * (0.9 + 0.5 * h4)) + ph * 0.7 + 1.7);
        let r = c[0] * bb;
        let g = c[1] * bb;
        let bl = c[2] * bb;
        const lum = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
        r = lum + (r - lum) * sb;
        g = lum + (g - lum) * sb;
        bl = lum + (bl - lum) * sb;

        // each pole's hue drifts on its own period and phase, so the hues
        // shift relative to each other instead of the field sliding together
        if (M.hueBreath) {
            const hp = (M.hueBreathPeriod ?? 74) * (0.78 + 0.44 * h1);
            const d =
                M.hueBreath * Math.sin((TAU * t) / hp + ph * 0.9 + h3 * TAU);
            [r, g, bl] = rotateHue(r, g, bl, d);
        }

        // wash toward the background
        out.poleCol[i * 3] = Math.max(0, r + (bg[0] - r) * wash);
        out.poleCol[i * 3 + 1] = Math.max(0, g + (bg[1] - g) * wash);
        out.poleCol[i * 3 + 2] = Math.max(0, bl + (bg[2] - bl) * wash);
    }
};
